// NavBar.tsx
import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  ListItemIcon,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import LogoutIcon from '@mui/icons-material/Logout';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Constante pour la hauteur de la barre de navigation
export const NAVBAR_HEIGHT = 64;

export default function NavBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleShareDialogOpen = () => {
    setShareDialogOpen(true);
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setEmail('');
    setPhone('');
  };

  const handleShareViaEmail = async () => {
    // Validation de l'email
    if (!email || !email.trim()) {
      alert('Veuillez entrer une adresse e-mail valide');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Veuillez entrer une adresse e-mail valide');
      return;
    }

    try {
      // Fetch the download URL from the backend avec authentification
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.lnterprise.com';

      const response = await fetch(`${apiUrl}/api/apk/url`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }
      const data = await response.json();
      const apkUrl = data.downloadUrl;

      const subject = encodeURIComponent('Application Mobile Sosec Truck');
      const body = encodeURIComponent(
        `Voici le lien pour télécharger l'application mobile Sosec Truck : ${apkUrl}`
      );
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
      handleShareDialogClose();
    } catch (error) {
      console.error('Error getting download URL:', error);
      // Fallback to Google Drive link
      const apkUrl =
        'https://drive.google.com/file/d/19VHhm1lzAsnwRT9ephv1WyjTAvuZ0yue/view?usp=drive_link';
      const subject = encodeURIComponent('Application Mobile Sosec Truck');
      const body = encodeURIComponent(
        `Voici le lien pour télécharger l'application mobile Sosec Truck : ${apkUrl}`
      );
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
      handleShareDialogClose();
    }
  };

  const handleShareViaWhatsApp = async () => {
    // Validation du numéro de téléphone
    if (!phone || !phone.trim()) {
      alert('Veuillez entrer un numéro de téléphone WhatsApp valide');
      return;
    }

    // Vérifier que le numéro contient uniquement des chiffres (avec le code pays)
    const phoneRegex = /^\d{10,15}$/;
    const cleanPhone = phone.replace(/\s+/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      alert('Veuillez entrer un numéro de téléphone valide (ex: 221771234567)');
      return;
    }

    try {
      // Fetch the download URL from the backend avec authentification
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.lnterprise.com';

      const response = await fetch(`${apiUrl}/api/apk/url`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }
      const data = await response.json();
      const apkUrl = data.downloadUrl;

      const message = encodeURIComponent(
        `Voici le lien pour télécharger l'application mobile Sosec Truck : ${apkUrl}`
      );
      window.open(`https://wa.me/${cleanPhone}?text=${message}`);
      handleShareDialogClose();
    } catch (error) {
      console.error('Error getting download URL:', error);
      // Fallback to Google Drive link
      const apkUrl =
        'https://drive.google.com/file/d/19VHhm1lzAsnwRT9ephv1WyjTAvuZ0yue/view?usp=drive_link';
      const message = encodeURIComponent(
        `Voici le lien pour télécharger l'application mobile Sosec Truck : ${apkUrl}`
      );
      window.open(`https://wa.me/${cleanPhone}?text=${message}`);
      handleShareDialogClose();
    }
  };

  const handleDownloadApk = async () => {
    try {
      // Fetch the download URL from the backend avec authentification
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.lnterprise.com';

      const response = await fetch(`${apiUrl}/api/apk/url`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }
      const data = await response.json();
      const apkUrl = data.downloadUrl;

      // Open the download URL in a new tab
      window.open(apkUrl, '_blank');
    } catch (error) {
      console.error('Error getting download URL:', error);
      // Fallback to Google Drive link
      const apkUrl =
        'https://drive.google.com/file/d/19VHhm1lzAsnwRT9ephv1WyjTAvuZ0yue/view?usp=drive_link';
      window.open(apkUrl, '_blank');
    }
  };

  if (!user) return null;

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1, // S'assure que la NavBar est toujours au-dessus
          bgcolor: 'primary.main',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          height: NAVBAR_HEIGHT,
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            L&N ENTREPRISE
          </Typography>
          <Tooltip title="Télécharger l'APK">
            <IconButton
              color="inherit"
              onClick={handleDownloadApk}
              sx={{ mr: 1 }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Partager l'APK">
            <IconButton
              color="inherit"
              onClick={handleShareDialogOpen}
              sx={{ mr: 1 }}
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box>
            <Tooltip title="Compte">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  {user.email ? user.email.charAt(0).toUpperCase() : '?'}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              anchorEl={anchorElUser}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <Box sx={{ px: 2, py: 1.5, maxWidth: 300 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Connecté en tant que
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {user.email}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Rôle: {user.role}
                </Typography>
                {/* <Typography variant="caption" color="text.secondary">ID: {user.id}</Typography> */}
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleCloseUserMenu();
                  logout();
                  navigate('/login');
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Déconnexion
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          zIndex: theme.zIndex.drawer,
        }}
      >
        <Box
          sx={{
            width: 250,
            bgcolor: 'background.paper',
            pt: `${NAVBAR_HEIGHT}px`,
          }}
        >
          <List>
            <ListItem
              onClick={() => {
                logout();
                navigate('/login');
                setDrawerOpen(false);
              }}
            >
              <ListItemText primary="Déconnexion" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
      {/* Dialog de partage */}
      <Dialog open={shareDialogOpen} onClose={handleShareDialogClose}>
        <DialogTitle>Partager l'application mobile</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Partagez le lien de téléchargement de l'application mobile via email
            ou WhatsApp.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Adresse email"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Numéro WhatsApp (avec code pays, ex: 221XXXXXXXXX)"
            type="tel"
            fullWidth
            variant="outlined"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleShareDialogClose}>Annuler</Button>
          <Button
            onClick={handleShareViaEmail}
            disabled={!email}
            startIcon={<EmailIcon />}
          >
            Partager par Email
          </Button>
          <Button
            onClick={handleShareViaWhatsApp}
            disabled={!phone}
            startIcon={<WhatsAppIcon />}
          >
            Partager par WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
      {/* Espace pour éviter que le contenu ne soit masqué sous la barre de navigation */}
      <Toolbar sx={{ minHeight: `${NAVBAR_HEIGHT}px !important` }} />
    </>
  );
}
